import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../hooks/useApi';
import {
  CurrencyIcon, ReceiptIcon, TrendUpIcon, RefreshIcon,
  CheckIcon, ClockIcon, AlertIcon, WalletIcon,
} from '../../components/Icons';

const fmt = v => parseFloat(v || 0).toLocaleString('pt-PT');

// ── Bancos angolanos — prefixo raw (8 chars) conforme Aviso BNA 06/2015 ──
// IBAN Angola: 25 chars → AO + 2 dígitos controlo + 21 dígitos NBA
// Representação escrita: grupos de 4, separados por espaço, último grupo 1 char
// Exemplo: AO06 0040 XXXX XXXX XXXX XXXX X
const BANCOS_AO = [
  { nome: 'Banco Angolano de Investimentos',       sigla: 'BAI',   raw: 'AO060040' },
  { nome: 'Banco Yetu',                            sigla: 'YETU',  raw: 'AO060066' },
  { nome: 'Banco Angolano de Negócios e Comércio', sigla: 'BANC',  raw: 'AO060053' },
  { nome: 'Banco BAI Microfinanças',               sigla: 'BMF',   raw: 'AO060048' },
  { nome: 'Banco BIC',                             sigla: 'BIC',   raw: 'AO060051' },
  { nome: 'Banco Caixa Geral de Angola',           sigla: 'BCGA',  raw: 'AO060004' },
  { nome: 'Banco Comercial Angolano',              sigla: 'BCA',   raw: 'AO060043' },
  { nome: 'Banco Comercial do Huambo',             sigla: 'BCH',   raw: 'AO060059' },
  { nome: 'Banco de Comércio e Indústria',         sigla: 'BCI',   raw: 'AO060005' },
  { nome: 'Banco de Crédito do Sul',               sigla: 'BCS',   raw: 'AO060070' },
  { nome: 'Banco de Desenvolvimento Angola',       sigla: 'BDA',   raw: 'AO060054' },
  { nome: 'Banco de Fomento Angola',               sigla: 'BFA',   raw: 'AO060006' },
  { nome: 'Banco de Investimento Rural',           sigla: 'BIR',   raw: 'AO060067' },
  { nome: 'Banco de Negócios Internacional',       sigla: 'BNI',   raw: 'AO060052' },
  { nome: 'Banco de Poupança e Crédito',           sigla: 'BPC',   raw: 'AO060010' },
  { nome: 'Banco da China',                        sigla: 'BOCLB', raw: 'AO060071' },
  { nome: 'Banco Económico',                       sigla: 'BE',    raw: 'AO060045' },
  { nome: 'Banco Keve',                            sigla: 'KEVE',  raw: 'AO060047' },
  { nome: 'Banco Kwanza Investimento',             sigla: 'BKI',   raw: 'AO060057' },
  { nome: 'Banco Mais',                            sigla: 'BMAIS', raw: 'AO060065' },
  { nome: 'Banco Millennium Atlântico',            sigla: 'BPA',   raw: 'AO060055' },
  { nome: 'Banco Postal',                          sigla: 'BPT',   raw: 'AO060069' },
  { nome: 'Banco Prestígio',                       sigla: 'BPG',   raw: 'AO060064' },
  { nome: 'Banco Sol',                             sigla: 'BSOL',  raw: 'AO060044' },
  { nome: 'Banco Valor',                           sigla: 'BVB',   raw: 'AO060062' },
  { nome: 'Banco VTB África',                      sigla: 'VTB',   raw: 'AO060056' },
  { nome: 'Finibanco Angola',                      sigla: 'FNB',   raw: 'AO060058' },
  { nome: 'Standard Bank de Angola',               sigla: 'SBA',   raw: 'AO060060' },
  { nome: 'Standard Chartered Bank de Angola',     sigla: 'SCBA',  raw: 'AO060063' },
];

// ── Formatar IBAN conforme Aviso BNA 06/2015, Art. 5.º, n.º 4 ────────────
// Grupos de 4 caracteres separados por espaço; último grupo tem 1 carácter
// Total: 25 chars → "XXXX XXXX XXXX XXXX XXXX XXXX X"
const formatarIBAN = (valor) => {
  // Só alfanuméricos, uppercase, máx 25 chars
  const raw = valor.replace(/[^A-Z0-9]/gi, '').toUpperCase().substring(0, 25);
  const grupos = [];
  for (let i = 0; i < raw.length; i += 4) {
    grupos.push(raw.substring(i, i + 4));
  }
  return grupos.join(' ');
};

// Desformatar (remover espaços) para comparações internas
const rawIBAN = (iban) => (iban || '').replace(/\s/g, '').toUpperCase();

// Prefixo formatado de um banco (ex: "AO06 0040 ")
const prefixoFormatado = (nomeBanco) => {
  const b = BANCOS_AO.find(b => b.nome === nomeBanco || b.sigla === nomeBanco);
  return b ? formatarIBAN(b.raw) + ' ' : '';
};

// Componente reutilizável: select de banco + IBAN auto-formatado
function CampoBancoIban({ banco, iban, titular, onChangeBanco, onChangeIban, onChangeTitular }) {
  const bancoInfo   = BANCOS_AO.find(b => b.nome === banco);
  const prefRaw     = bancoInfo?.raw || '';
  const prefDisplay = bancoInfo ? formatarIBAN(prefRaw) : '';

  const handleBanco = (e) => {
    const val = e.target.value;
    onChangeBanco(val);
    const b = BANCOS_AO.find(b => b.nome === val);
    if (b) {
      // Pré-preenche sempre que o IBAN estiver vazio ou for apenas o prefixo de outro banco
      const ibanRaw = rawIBAN(iban);
      const eraOutroPrefixo = BANCOS_AO.some(x => ibanRaw === x.raw);
      if (!iban || eraOutroPrefixo) {
        onChangeIban(formatarIBAN(b.raw) + ' ');
      }
    }
  };

  const handleIban = (e) => {
    const inputRaw = rawIBAN(e.target.value);
    // Protege o prefixo: não deixa apagar os primeiros 8 chars do banco
    if (prefRaw && !inputRaw.startsWith(prefRaw)) return;
    onChangeIban(formatarIBAN(inputRaw));
  };

  // Exemplo visual do formato completo
  const exemplo = bancoInfo
    ? `${formatarIBAN(bancoInfo.raw + 'XXXXXXXXXXXXXXXXX').substring(0, 32).trim()}`
    : 'AO06 XXXX XXXX XXXX XXXX XXXX X';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div>
        <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Titular da conta *</label>
        <input
          className="input" value={titular}
          onChange={e => onChangeTitular(e.target.value)}
          placeholder="Nome completo do titular"
        />
      </div>
      <div>
        <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Banco *</label>
        <select className="input" value={banco} onChange={handleBanco}>
          <option value="">— Selecciona o banco —</option>
          {BANCOS_AO.map(b => (
            <option key={b.sigla} value={b.nome}>
              {b.sigla} · {b.nome}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>IBAN *</label>
        <div style={{ position: 'relative' }}>
          <input
            className="input" value={iban}
            onChange={handleIban}
            maxLength={31} // 25 chars + 6 espaços
            placeholder="Selecciona o banco para pré-preencher"
            style={{
              fontFamily: '"Courier New", Courier, monospace',
              letterSpacing: '1px',
              fontSize: '13px',
            }}
          />
          {bancoInfo && iban && (
            <div style={{
              position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
              fontSize: '10px', fontWeight: '600',
              color: rawIBAN(iban).length === 25 ? 'var(--success)' : 'var(--text-muted)',
            }}>
              {rawIBAN(iban).length}/25
            </div>
          )}
        </div>
        {/* Hint com formato visual */}
        <div style={{
          marginTop: '5px', padding: '6px 10px', borderRadius: '6px',
          background: 'var(--surface)', border: '1px solid var(--border)',
          fontSize: '11px', fontFamily: '"Courier New", Courier, monospace',
          letterSpacing: '0.5px', color: 'var(--text-muted)',
          display: 'flex', gap: '2px', flexWrap: 'wrap', alignItems: 'center',
        }}>
          {bancoInfo
            ? (
              <>
                <span style={{ background: '#DBEAFE', color: '#1D4ED8', borderRadius: '3px', padding: '1px 4px', fontWeight: '700' }}>
                  {formatarIBAN(bancoInfo.raw).substring(0, 4)}
                </span>
                {' '}
                <span style={{ background: '#FEF3C7', color: '#92400E', borderRadius: '3px', padding: '1px 4px', fontWeight: '700' }}>
                  {formatarIBAN(bancoInfo.raw).substring(5, 9)}
                </span>
                {' '}
                <span style={{ color: 'var(--text-muted)' }}>XXXX XXXX XXXX XXXX X</span>
              </>
            )
            : <span>AO06 XXXX XXXX XXXX XXXX XXXX X</span>
          }
          <span style={{ marginLeft: '6px', fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'inherit' }}>
            (25 caracteres · Aviso BNA 06/2015)
          </span>
        </div>
      </div>
    </div>
  );
}

// Prefixo para validação do botão de submeter
const prefixoDoBanco = (nomeBanco) => {
  const b = BANCOS_AO.find(b => b.nome === nomeBanco || b.sigla === nomeBanco);
  return b ? formatarIBAN(b.raw) : '';
};

function BadgeTipo({ tipo }) {
  const map = {
    credito:      { label: 'Crédito',      bg: '#F0FDF4', color: '#16A34A' },
    debito:       { label: 'Levantamento', bg: '#FFF7ED', color: '#EA580C' },
    pendente:     { label: 'Pendente',     bg: '#FFFBEB', color: '#D97706' },
    cancelamento: { label: 'Cancelado',    bg: '#FEF2F2', color: '#DC2626' },
  };
  const s = map[tipo] || { label: tipo, bg: '#F3F4F6', color: '#6B7280' };
  return (
    <span style={{ padding: '2px 8px', borderRadius: '99px', fontSize: '10px', fontWeight: '600', background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function BadgeEstado({ estado }) {
  const map = {
    pendente:         { label: 'Pendente',           bg: '#FFFBEB', color: '#D97706' },
    em_processamento: { label: 'Em processamento',   bg: '#EFF6FF', color: '#2563EB' },
    pago:             { label: 'Pago',               bg: '#F0FDF4', color: '#16A34A' },
    rejeitado:        { label: 'Rejeitado',          bg: '#FEF2F2', color: '#DC2626' },
  };
  const s = map[estado] || { label: estado, bg: '#F3F4F6', color: '#6B7280' };
  return (
    <span style={{ padding: '2px 8px', borderRadius: '99px', fontSize: '10px', fontWeight: '600', background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function ModalSolicitar({ wallet, onClose, onSucesso }) {
  const saldoDisponivel = parseFloat(wallet?.saldo || 0) - parseFloat(wallet?.saldo_solicitado || 0);
  const [valor,   setValor]   = useState('');
  const [iban,    setIban]    = useState(wallet?.iban    || '');
  const [banco,   setBanco]   = useState(wallet?.banco   || '');
  const [titular, setTitular] = useState(wallet?.titular || '');
  const [notas,   setNotas]   = useState('');
  const [loading, setLoading] = useState(false);
  const [erro,    setErro]    = useState(null);

  const submeter = async () => {
    setLoading(true);
    setErro(null);
    try {
      const data = await apiFetch(
        '/fornecedor/saldo/solicitar',
        'POST',
        { valor: parseFloat(valor), iban: rawIBAN(iban), banco, titular, notas }
      );
      if (data.sucesso) {
        onSucesso(data.mensagem);
        onClose();
      } else {
        setErro(data.mensagem || 'Erro ao submeter');
      }
    } catch (e) {
      setErro(e.message || 'Erro de ligação ao servidor');
    } finally {
      setLoading(false);
    }
  };

  const ibanValido = rawIBAN(iban).length === 25;
  const podeSolicitar = !loading && !!valor && parseFloat(valor) > 0
    && parseFloat(valor) <= saldoDisponivel && !!banco && ibanValido;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '16px',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--card)', borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        width: '100%', maxWidth: '500px',
        maxHeight: '90vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>

        {/* ── Header fixo ──────────────────────────────── */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '700' }}>Solicitar Levantamento</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
              O pedido fica pendente até o administrador processar o pagamento
            </div>
          </div>
          {/* X — fechar */}
          <button
            onClick={onClose}
            style={{
              width: '30px', height: '30px', borderRadius: '50%', border: 'none',
              background: 'var(--surface)', cursor: 'pointer', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '16px', color: 'var(--text-muted)', marginLeft: '12px',
              lineHeight: 1, transition: 'background .15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}
            title="Fechar"
          >
            ×
          </button>
        </div>

        {/* ── Corpo com scroll ──────────────────────────── */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>

          {/* Saldo disponível */}
          <div style={{
            background: 'var(--surface)', borderRadius: 'var(--radius)',
            padding: '14px 16px', marginBottom: '18px',
          }}>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
              Saldo disponível para levantamento
            </div>
            <div style={{ fontSize: '26px', fontWeight: '800', color: 'var(--success)' }}>
              {fmt(saldoDisponivel)} Kz
            </div>
            {parseFloat(wallet?.saldo_solicitado || 0) > 0 && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <ClockIcon size={10} /> {fmt(wallet.saldo_solicitado)} Kz já em pedidos pendentes
              </div>
            )}
          </div>

          {/* Valor */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
              Valor a levantar (Kz) *
            </label>
            <input
              type="number" min="1" step="100" className="input"
              value={valor} onChange={e => setValor(e.target.value)}
              placeholder={`Máx. ${fmt(saldoDisponivel)} Kz`}
            />
          </div>

          {/* Dados bancários */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px', marginBottom: '14px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
              Conta bancária de destino
            </div>
            <CampoBancoIban
              banco={banco} iban={iban} titular={titular}
              onChangeBanco={setBanco} onChangeIban={setIban} onChangeTitular={setTitular}
            />
            <div style={{ marginTop: '12px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>Notas (opcional)</label>
              <input className="input" value={notas} onChange={e => setNotas(e.target.value)} placeholder="Referência ou observações" />
            </div>
          </div>

          {/* Erro */}
          {erro && (
            <div style={{
              padding: '10px 14px', borderRadius: 'var(--radius)',
              background: '#FEF2F2', color: '#DC2626', fontSize: '12px',
              border: '1px solid #FECACA', display: 'flex', alignItems: 'center', gap: '8px',
              marginBottom: '10px',
            }}>
              <AlertIcon size={13} color="#DC2626" /> {erro}
            </div>
          )}

          {/* Info */}
          <div style={{
            padding: '10px 14px', borderRadius: 'var(--radius)',
            background: '#EFF6FF', color: '#1D4ED8', fontSize: '11px',
            border: '1px solid #BFDBFE',
          }}>
            O administrador processa os pagamentos manualmente. Serás notificado quando o pagamento for efectuado.
          </div>
        </div>

        {/* ── Rodapé fixo com botões ────────────────────── */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--border)',
          display: 'flex', gap: '8px', justifyContent: 'flex-end',
          flexShrink: 0, background: 'var(--card)', borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
        }}>
          <button className="btn btn-outline" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={submeter}
            disabled={!podeSolicitar}
            style={{ minWidth: '170px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
          >
            {loading
              ? <><ClockIcon size={13} /> A submeter...</>
              : <><WalletIcon size={13} color="#fff" /> Solicitar levantamento</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Ganhos() {
  const [wallet,       setWallet]       = useState(null);
  const [transacoes,   setTransacoes]   = useState([]);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [load,         setLoad]         = useState(true);
  const [tab,          setTab]          = useState('movimentos');
  const [modal,        setModal]        = useState(false);
  const [toast,        setToast]        = useState(null);
  const [config,       setConfig]       = useState({ iban: '', banco: '', titular: '' });
  const [savingConf,   setSavingConf]   = useState(false);
  const [msgConf,      setMsgConf]      = useState(null);

  const carregar = useCallback(async () => {
    setLoad(true);
    try {
      const data = await apiFetch('/fornecedor/saldo');
      if (data.sucesso) {
        setWallet(data.wallet);
        setTransacoes(data.transacoes || []);
        setSolicitacoes(data.solicitacoes || []);
        setConfig({
          iban:    data.wallet.iban    || '',
          banco:   data.wallet.banco   || '',
          titular: data.wallet.titular || '',
        });
      }
    } catch {}
    finally { setLoad(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const mostrarToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const guardarDadosBancarios = async () => {
    setSavingConf(true);
    setMsgConf(null);
    try {
      const data = await apiFetch(
        '/fornecedor/saldo/configurar',
        'PUT',
        { iban: rawIBAN(config.iban), banco: config.banco, titular: config.titular }
      );
      if (data.sucesso) {
        setWallet(data.wallet);
        setMsgConf({ tipo: 'sucesso', texto: 'Dados bancários guardados!' });
      } else {
        setMsgConf({ tipo: 'erro', texto: data.mensagem || 'Erro ao guardar' });
      }
    } catch (e) {
      setMsgConf({ tipo: 'erro', texto: e.message || 'Erro de ligação ao servidor' });
    } finally {
      setSavingConf(false);
      setTimeout(() => setMsgConf(null), 3000);
    }
  };

  const saldo            = parseFloat(wallet?.saldo            || 0);
  const saldoSolicitado  = parseFloat(wallet?.saldo_solicitado || 0);
  const saldoDisponivel  = Math.max(saldo - saldoSolicitado, 0);
  const totalRecebido    = parseFloat(wallet?.total_recebido   || 0);
  const totalPago        = parseFloat(wallet?.total_pago       || 0);
  const pedidosPendentes = solicitacoes.filter(s => ['pendente','em_processamento'].includes(s.estado));

  return (
    <>
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 2000,
          background: '#16A34A', color: '#fff', padding: '12px 18px',
          borderRadius: 'var(--radius-lg)', fontSize: '13px', fontWeight: '600',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <CheckIcon size={14} color="#fff" /> {toast}
        </div>
      )}

      {modal && (
        <ModalSolicitar
          wallet={wallet}
          onClose={() => setModal(false)}
          onSucesso={msg => { mostrarToast(msg); carregar(); }}
        />
      )}

      <div className="topbar">
        <div>
          <div className="topbar-title">Saldo & Ganhos</div>
          <div className="topbar-sub">Saldo a receber — transferível para a tua conta bancária a qualquer momento</div>
        </div>
        <div className="topbar-actions" style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-outline" onClick={carregar} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <RefreshIcon size={14} /> Actualizar
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setModal(true)}
            disabled={saldoDisponivel <= 0}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <WalletIcon size={14} color="#fff" /> Solicitar levantamento
          </button>
        </div>
      </div>

      <div className="page-content">

        {/* ── KPIs ───────────────────────────────────────────── */}
        <div className="stats-grid" style={{ marginBottom: '20px' }}>
          {[
            {
              icon: <WalletIcon />, label: 'Saldo disponível',
              value: `${fmt(saldoDisponivel)} Kz`,
              sub: saldoSolicitado > 0 ? `+ ${fmt(saldoSolicitado)} Kz em pedidos` : 'Pronto para solicitar',
              bg: '#F0FDF4', ic: '#16A34A',
            },
            {
              icon: <ClockIcon />, label: 'Em processamento',
              value: `${fmt(saldoSolicitado)} Kz`,
              sub: `${pedidosPendentes.length} pedido(s) pendente(s)`,
              bg: '#FFFBEB', ic: '#D97706',
            },
            {
              icon: <TrendUpIcon />, label: 'Total recebido',
              value: `${fmt(totalRecebido)} Kz`,
              sub: `${transacoes.filter(t => t.tipo === 'credito').length} créditos`,
              bg: '#EFF6FF', ic: '#2563EB',
            },
            {
              icon: <ReceiptIcon />, label: 'Total levantado',
              value: `${fmt(totalPago)} Kz`,
              sub: `${solicitacoes.filter(s => s.estado === 'pago').length} pagamentos`,
              bg: '#FFF7ED', ic: '#F97316',
            },
          ].map((c, i) => (
            <div key={i} className="stat-card">
              <div className="stat-icon" style={{ background: c.bg }}>
                {React.cloneElement(c.icon, { size: 18, color: c.ic })}
              </div>
              <div className="stat-value" style={{ marginTop: '8px' }}>{c.value}</div>
              <div className="stat-label">{c.label}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{c.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Info box ───────────────────────────────────────── */}
        <div style={{
          background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 'var(--radius-lg)',
          padding: '12px 16px', marginBottom: '16px', fontSize: '12px', color: '#1D4ED8',
          display: 'flex', alignItems: 'flex-start', gap: '10px',
        }}>
          <WalletIcon size={15} color="#2563EB" style={{ flexShrink: 0, marginTop: '1px' }} />
          <div>
            <strong>Como funciona:</strong> O teu saldo a receber acumula automaticamente a cada reserva concluída. Podes solicitar a transferência para a tua conta bancária a qualquer momento — o administrador processa os pagamentos manualmente e receberás uma notificação quando for efectuado.
            <div style={{ marginTop: '6px', fontSize: '11px', color: '#1E40AF', fontStyle: 'italic' }}>
              Este saldo é um registo contabilístico interno. Não constitui moeda eletrónica nem depósito bancário. Só pode ser transferido para conta bancária titulada em teu nome.
            </div>
          </div>
        </div>

        {/* ── Tabs ───────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
          {[
            { key: 'movimentos',   label: 'Movimentos' },
            { key: 'pedidos',      label: `Pedidos${pedidosPendentes.length > 0 ? ` (${pedidosPendentes.length})` : ''}` },
            { key: 'conta',        label: 'Conta bancária' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '7px 16px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer',
                fontSize: '12px', fontWeight: tab === t.key ? '600' : '400',
                background: tab === t.key ? 'var(--orange)' : 'var(--surface)',
                color: tab === t.key ? '#fff' : 'var(--text-secondary)',
                position: 'relative',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Tab: Movimentos ────────────────────────────────── */}
        {tab === 'movimentos' && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ReceiptIcon size={15} color="var(--orange)" />
              <span style={{ fontSize: '13px', fontWeight: '600' }}>Histórico de movimentos</span>
            </div>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr><th>Data</th><th>Tipo</th><th>Descrição</th><th>Valor</th><th>Saldo após</th></tr>
                </thead>
                <tbody>
                  {load ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>A carregar...</td></tr>
                  ) : transacoes.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                      <ReceiptIcon size={28} color="var(--border)" /><br />
                      <span style={{ marginTop: '8px', display: 'block', fontSize: '13px' }}>Ainda sem movimentos</span>
                    </td></tr>
                  ) : transacoes.map(t => (
                    <tr key={t.id}>
                      <td style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {new Date(t.created_at).toLocaleDateString('pt-PT')}<br />
                        <span style={{ fontSize: '10px' }}>{new Date(t.created_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}</span>
                      </td>
                      <td><BadgeTipo tipo={t.tipo} /></td>
                      <td style={{ fontSize: '12px', maxWidth: '220px' }}>
                        <div style={{ fontWeight: '500' }}>{t.descricao || '—'}</div>
                        {t.anuncio_titulo && <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{t.anuncio_titulo}</div>}
                      </td>
                      <td style={{
                        fontSize: '13px', fontWeight: '700', whiteSpace: 'nowrap',
                        color: t.tipo === 'credito' ? 'var(--success)' : t.tipo === 'debito' ? '#EA580C' : 'var(--text-muted)',
                      }}>
                        {t.tipo === 'credito' ? '+' : t.tipo === 'debito' ? '-' : ''}{fmt(t.valor)} Kz
                      </td>
                      <td style={{ fontSize: '12px', fontWeight: '600' }}>{fmt(t.saldo_apos)} Kz</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Tab: Pedidos de levantamento ───────────────────── */}
        {tab === 'pedidos' && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <WalletIcon size={15} color="var(--orange)" />
                <span style={{ fontSize: '13px', fontWeight: '600' }}>Pedidos de levantamento</span>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => setModal(true)}
                disabled={saldoDisponivel <= 0}
                style={{ fontSize: '11px', padding: '5px 14px', display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                <CurrencyIcon size={12} color="#fff" /> Novo pedido
              </button>
            </div>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr><th>Data</th><th>Valor</th><th>Banco / IBAN</th><th>Estado</th><th>Notas admin</th></tr>
                </thead>
                <tbody>
                  {load ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>A carregar...</td></tr>
                  ) : solicitacoes.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                      <WalletIcon size={28} color="var(--border)" /><br />
                      <span style={{ marginTop: '8px', display: 'block', fontSize: '13px' }}>Nenhum pedido submetido ainda</span>
                    </td></tr>
                  ) : solicitacoes.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {new Date(s.created_at).toLocaleDateString('pt-PT')}<br />
                        <span style={{ fontSize: '10px' }}>{new Date(s.created_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}</span>
                      </td>
                      <td style={{ fontSize: '13px', fontWeight: '700', color: s.estado === 'pago' ? 'var(--success)' : 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                        {fmt(s.valor)} Kz
                      </td>
                      <td style={{ fontSize: '11px' }}>
                        <div style={{ fontWeight: '500' }}>{s.banco || '—'}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '10px', fontFamily: 'monospace' }}>{s.iban || ''}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>{s.titular || ''}</div>
                      </td>
                      <td><BadgeEstado estado={s.estado} /></td>
                      <td style={{ fontSize: '11px', color: 'var(--text-muted)', maxWidth: '160px' }}>
                        {s.notas_admin || (s.referencia ? `Ref: ${s.referencia}` : '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Tab: Conta bancária ────────────────────────────── */}
        {tab === 'conta' && (
          <div className="card" style={{ maxWidth: '480px' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CurrencyIcon size={16} color="var(--orange)" />
              Dados bancários padrão
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '18px' }}>
              Estes dados serão pré-preenchidos nos pedidos de levantamento
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <CampoBancoIban
                banco={config.banco}
                iban={config.iban}
                titular={config.titular}
                onChangeBanco={v => setConfig(c => ({ ...c, banco: v }))}
                onChangeIban={v  => setConfig(c => ({ ...c, iban: v }))}
                onChangeTitular={v => setConfig(c => ({ ...c, titular: v }))}
              />

              {msgConf && (
                <div style={{
                  padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: '12px', fontWeight: '500',
                  background: msgConf.tipo === 'sucesso' ? '#F0FDF4' : '#FEF2F2',
                  color: msgConf.tipo === 'sucesso' ? '#16A34A' : '#DC2626',
                  border: `1px solid ${msgConf.tipo === 'sucesso' ? '#BBF7D0' : '#FECACA'}`,
                  display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                  {msgConf.tipo === 'sucesso' ? <CheckIcon size={13} color="#16A34A" /> : <AlertIcon size={13} color="#DC2626" />}
                  {msgConf.texto}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-primary" onClick={guardarDadosBancarios} disabled={savingConf} style={{ minWidth: '150px' }}>
                  {savingConf ? <><ClockIcon size={13} /> A guardar...</> : <><CheckIcon size={13} color="#fff" /> Guardar dados</>}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}
